import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MdPayment, MdLock } from 'react-icons/md';
import { authenticatedFetch, apiFetchUrl } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../shared/Toast';
import { useAuth } from '../../context/AuthContext';

interface AdminBuyerPaymentSectionProps {
  poId: string;
  po: any;
}

const statusStyles: Record<string, string> = {
  paid: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-blue-100 text-blue-800',
  processing: 'bg-indigo-100 text-indigo-800',
  requested: 'bg-indigo-100 text-indigo-800',
  locked: 'bg-gray-100 text-gray-600',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

type PayMode = 'stripe' | 'razorpay' | 'bank';

export function AdminBuyerPaymentSection({ poId, po }: AdminBuyerPaymentSectionProps) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [payTarget, setPayTarget] = useState<any | null>(null);
  const [payMode, setPayMode] = useState<PayMode>('stripe');
  const [txRef, setTxRef] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin-buyer-payment-schedule', poId],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/payment-schedule`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load payment schedule');
      }
      const json = await res.json();
      return json.data;
    },
    enabled: !!poId,
  });

  const paymentUnlocked =
    data?.paymentUnlocked === true || po?.metadata?.paymentUnlocked === true;
  const milestones = data?.milestones || [];
  const summary = data?.summary || { pending: 0, paid: 0, upcoming: 0, currency: po?.currency || 'USD' };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-buyer-payment-schedule', poId] });
    queryClient.invalidateQueries({ queryKey: ['admin-po-details'] });
    queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-po-enriched'] });
    queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-purchase-order'] });
  };

  const payMutation = useMutation({
    mutationFn: async ({
      milestoneId,
      preferredGateway,
    }: {
      milestoneId: string;
      preferredGateway: 'stripe' | 'razorpay';
    }) => {
      const res = await authenticatedFetch(`/api/v1/admin/payment-milestones/${milestoneId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferredGateway }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to start payment');
      return json.data;
    },
    onSuccess: (result) => {
      const url =
        result?.payment?.stripeSessionUrl ||
        result?.payment?.checkoutUrl ||
        result?.payment?.stripeSession?.url;
      if (url) {
        showToast('Redirecting to secure checkout…', 'info');
        window.location.href = url;
        return;
      }
      const rzpOrder = result?.payment?.razorpayOrder;
      if (rzpOrder && (window as any).Razorpay) {
        const options = {
          key: rzpOrder.key,
          amount: rzpOrder.amount,
          currency: rzpOrder.currency,
          name: 'Euroasiann ERP',
          description: payTarget?.label || 'Order payment',
          order_id: rzpOrder.id,
          handler: async function (response: any) {
            try {
              const verifyResponse = await fetch(apiFetchUrl('/api/v1/payments/verify'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
                body: JSON.stringify({
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                  signature: response.razorpay_signature,
                }),
              });
              const verifyData = await verifyResponse.json();
              if (verifyData.success) {
                showToast('Payment successful', 'success');
                setPayTarget(null);
                invalidate();
              } else {
                showToast(verifyData.error || 'Payment verification failed', 'error');
              }
            } catch {
              showToast('Error verifying payment', 'error');
            }
          },
          prefill: {
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email,
          },
          theme: { color: '#0088cc' },
        };
        const rzp = new (window as any).Razorpay(options);
        rzp.open();
        return;
      }
      invalidate();
      showToast('Payment initiated', 'success');
      setPayTarget(null);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const bankProofMutation = useMutation({
    mutationFn: async () => {
      if (!payTarget) throw new Error('No milestone selected');
      const quotationId =
        typeof po?.quotationId === 'object' ? po.quotationId?._id : po?.quotationId;
      if (!quotationId) throw new Error('Quotation reference missing for bank proof');
      if (files.length === 0) throw new Error('Upload at least one payment proof document');

      const formData = new FormData();
      formData.append('quotationId', String(quotationId));
      formData.append('paymentAmount', String(payTarget.amount));
      formData.append('currency', payTarget.currency || po?.currency || 'USD');
      formData.append('paymentDate', new Date().toISOString().split('T')[0]);
      formData.append('paymentMethod', 'Bank Transfer');
      formData.append('transactionReference', txRef);
      formData.append('notes', notes || `Milestone: ${payTarget.label}`);
      formData.append('paymentStage', payTarget.type === 'advance' ? 'advance' : 'balance');
      formData.append('milestoneId', String(payTarget._id));
      files.forEach((f) => formData.append('proofDocuments', f));

      const res = await authenticatedFetch('/api/v1/admin/buyer/payment-proof', {
        method: 'POST',
        body: formData,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to submit payment proof');
      return json;
    },
    onSuccess: () => {
      showToast('Payment proof submitted. Vendor will verify.', 'success');
      setPayTarget(null);
      setTxRef('');
      setNotes('');
      setFiles([]);
      invalidate();
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  return (
    <div className="rounded-2xl border border-[#0088cc]/20 bg-[hsl(var(--card))] shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-[#0088cc]/15 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-bold flex items-center gap-2">
          <MdPayment className="w-5 h-5 text-[#0088cc]" />
          Payment
        </h2>
        {!paymentUnlocked ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase bg-amber-50 text-amber-800">
            <MdLock className="w-3 h-3" /> Locked until final invoice
          </span>
        ) : (
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase bg-emerald-50 text-emerald-700">
            Payment available
          </span>
        )}
      </div>
      <div className="p-6 space-y-4">
        {isLoading ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
            Loading payment schedule…
          </p>
        ) : isError ? (
          <p className="text-sm text-rose-600 text-center py-4">
            {(error as Error)?.message || 'Failed to load payment schedule'}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl border border-[#0088cc]/15 p-3">
                <p className="text-[10px] font-bold uppercase text-[#0088cc]/65">Pending</p>
                <p className="font-bold">
                  {summary.currency} {Number(summary.pending || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border border-emerald-200 p-3">
                <p className="text-[10px] font-bold uppercase text-emerald-700">Paid</p>
                <p className="font-bold">
                  {summary.currency} {Number(summary.paid || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] p-3">
                <p className="text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">
                  Upcoming
                </p>
                <p className="font-bold">
                  {summary.currency} {Number(summary.upcoming || 0).toFixed(2)}
                </p>
              </div>
            </div>
            {milestones.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
                Payment milestones appear after the vendor acknowledges the purchase order.
              </p>
            ) : (
              milestones.map((m: any) => {
                const canPay =
                  paymentUnlocked &&
                  ['pending', 'requested', 'overdue', 'processing'].includes(m.status);
                return (
                  <div
                    key={m._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-[#0088cc]/15 bg-[#0088cc]/5"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold">{m.label}</p>
                        <span
                          className={cn(
                            'text-[10px] font-bold px-2 py-0.5 rounded-full uppercase',
                            statusStyles[m.status] || statusStyles.pending
                          )}
                        >
                          {m.status}
                        </span>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {m.percentage}% · {m.currency} {Number(m.amount || 0).toFixed(2)}
                      </p>
                    </div>
                    {canPay && (
                      <button
                        type="button"
                        onClick={() => {
                          setPayTarget(m);
                          setPayMode('stripe');
                        }}
                        className="rounded-xl bg-[#0088cc] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white"
                      >
                        Pay
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {payTarget && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border bg-[hsl(var(--card))] p-6 space-y-4">
            <h3 className="font-bold">Pay {payTarget.label}</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {payTarget.currency} {Number(payTarget.amount || 0).toFixed(2)}
            </p>
            <div className="flex flex-wrap gap-2">
              {(['stripe', 'razorpay', 'bank'] as PayMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setPayMode(mode)}
                  className={cn(
                    'rounded-xl border px-3 py-1.5 text-xs font-bold uppercase tracking-widest',
                    payMode === mode ? 'border-[#0088cc] bg-[#0088cc]/10 text-[#0088cc]' : ''
                  )}
                >
                  {mode === 'bank' ? 'Bank transfer' : mode}
                </button>
              ))}
            </div>
            {payMode === 'bank' && (
              <div className="space-y-2">
                <input
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  placeholder="Transaction reference"
                  value={txRef}
                  onChange={(e) => setTxRef(e.target.value)}
                />
                <textarea
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <div className="space-y-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[hsl(var(--foreground))] shadow-md transition-colors hover:border-[#0088cc]/50 hover:bg-[#0088cc]/5">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      className="sr-only"
                      onChange={(e) => setFiles(Array.from(e.target.files || []))}
                    />
                    Choose files
                  </label>
                  {files.length > 0 && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      {files.length} file{files.length === 1 ? '' : 's'} selected
                      {files[0]?.name ? `: ${files.map((f) => f.name).join(', ')}` : ''}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPayTarget(null)}
                className="rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-widest"
              >
                Cancel
              </button>
              {payMode === 'bank' ? (
                <button
                  type="button"
                  disabled={bankProofMutation.isPending}
                  onClick={() => bankProofMutation.mutate()}
                  className="rounded-xl bg-[#0088cc] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                >
                  Submit proof
                </button>
              ) : (
                <button
                  type="button"
                  disabled={payMutation.isPending}
                  onClick={() =>
                    payMutation.mutate({
                      milestoneId: payTarget._id,
                      preferredGateway: payMode,
                    })
                  }
                  className="rounded-xl bg-[#0088cc] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
